import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { logEvent } from 'firebase/analytics';
import { InputText } from 'primereact/inputtext';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputTextarea } from 'primereact/inputtextarea';
import { useRef, useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  Checkbox,
  TextInput,
  Typography,
  ButtonSize,
  PrimaryButton,
  ButtonVariant,
  SecondaryButton,
} from '@uni-of-exeter/digital.lib.react.ui.kit';

import './share-to-manager-modal.css';
import { analytics } from '../../../firebase';
import { useErrorContext } from '../../error';
import { useAuthContext } from '../../../auth';
import { APP_TEXT } from '../../../const/app-text';
import { usePostManagerEmail } from '../../../hooks/query/learning-api/use-resource';

// ------------------------------------------------------------

const shareToManagerFormSchema = z.object({
  to: z.email('Please enter a valid email address').min(1, 'Email is required'),
  cc: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        const emails = val.split(',').map((email) => email.trim());

        const isValidEmail = (email: string): boolean => {
          if (!email || email.length > 254) return false; // RFC 5321 limit

          const parts = email.split('@');
          if (parts.length !== 2) return false;

          const [localPart, domain] = parts;

          // Validate local part (before @)
          if (!localPart || localPart.length > 64 || localPart.length === 0) return false; // RFC 5321 limit
          if (/[^\w.!#$%&'*+/=?^`{|}~-]/.test(localPart)) return false;

          // Validate domain (after @)
          if (!domain || domain.length === 0) return false;
          const domainParts = domain.split('.');
          if (domainParts.length < 2) return false;

          // Check each domain part
          for (const part of domainParts) {
            if (!part || part.length === 0 || part.length > 63) return false; // RFC 1035 limit
            if (!/^[a-zA-Z0-9-]+$/.test(part)) return false;
            if (part.startsWith('-') || part.endsWith('-')) return false;
          }

          return true;
        };

        return emails.every((email) => email === '' || isValidEmail(email));
      },
      {
        message: 'Please enter valid email addresses separated by commas',
      }
    ),
  selectedRecommendations: z.array(z.string()).min(1, 'Please select at least one recommendation'),
  sendCopyToYourself: z.boolean().optional(),
});

type ShareToManagerFormData = z.infer<typeof shareToManagerFormSchema>;

export interface LearningRecommendation {
  id: string;
  title: string;
}

export interface ShareToManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  learningRecommendations: LearningRecommendation[];
  managerEmail?: string;
  currentUserFullName?: string;
  currentUserEmail?: string;
}

// Reusable components
const FormField = ({
  label,
  children,
  error,
  helperText,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  helperText?: string;
}) => (
  <div className="formField">
    <label className="fieldLabel">{label}</label>
    {helperText && <p className="helperText">{helperText}</p>}
    {children}
    {error && (
      <Typography
        variant="body.xs"
        className="errorText"
      >
        {error}
      </Typography>
    )}
  </div>
);

const ReadOnlyField = ({
  label,
  note,
  labelId,
  children,
}: {
  label: string;
  note: string;
  labelId: string;
  children: React.ReactNode;
}) => (
  <div className="readOnlyField" contentEditable="false">
    <label id={labelId} className="fieldLabel">{label}</label>
    <Typography
      variant="body.xs"
      className="readOnlyNote"
      role="heading"
      tabIndex={0}
    >
      {note}
    </Typography>
    {children}
  </div>
);

const RecommendationItem = ({
  recommendation,
  isSelected,
  onToggle,
}: {
  recommendation: LearningRecommendation;
  isSelected: boolean;
  onToggle: (id: string, checked: boolean) => void;
}) => (
  <div
    className="recommendationItem"
    onClick={() => onToggle(recommendation.id, !isSelected)}
  >
    <Checkbox
      checked={isSelected}
      onChange={(value) => onToggle(recommendation.id, value)}
    />
    <Typography
      variant="body.small"
      className="recommendationTitle"
    >
      {recommendation.title}
    </Typography>
  </div>
);

function ShareToManagerModal({
  isOpen,
  onClose,
  learningRecommendations,
  managerEmail = 'unknown',
  currentUserFullName = 'unknown',
  currentUserEmail = 'unknown',
}: ShareToManagerModalProps) {
  const { userId } = useAuthContext();

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, isValidating },
  } = useForm<ShareToManagerFormData>({
    resolver: zodResolver(shareToManagerFormSchema),
    defaultValues: {
      to: managerEmail,
      cc: '',
      selectedRecommendations: learningRecommendations.map((rec) => rec.id),
      sendCopyToYourself: true,
    },
    mode: 'onChange',
  });
  const { setError } = useErrorContext();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const hasInitialized = useRef(false);

  const selectedRecommendations = watch('selectedRecommendations');

  // Watch all form fields for validation
  const formData = watch();

  // Explicit validation check using Zod schema
  const isFormValid = useMemo(() => {
    try {
      shareToManagerFormSchema.parse(formData);
      return true;
    } catch {
      return false;
    }
  }, [formData]);

  // Only reset form when modal first opens
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      reset({
        to: managerEmail,
        cc: '',
        selectedRecommendations: learningRecommendations.map((rec) => rec.id),
        sendCopyToYourself: true,
      });
      hasInitialized.current = true;
      logEvent(analytics, 'open_share_to_manager', { custom_user_id: userId });
    } else if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [isOpen, managerEmail, learningRecommendations, reset, userId]);

  const emailContentStart = APP_TEXT.components.shareToManagerModal.emailContent.start;
  const emailContentEnd = APP_TEXT.components.shareToManagerModal.emailContent.end.replace(
    '[YOUR_NAME]',
    currentUserFullName
  );

  const handleDeselectAll = () => {
    setValue('selectedRecommendations', []);
    trigger('selectedRecommendations');
  };
  const handleSelectAll = () => {
    setValue(
      'selectedRecommendations',
      learningRecommendations.map((rec) => rec.id)
    );
    trigger('selectedRecommendations');
  };

  const handleRecommendationToggle = (recommendationId: string, checked: boolean) => {
    const currentSelected = watch('selectedRecommendations');
    if (checked) {
      setValue('selectedRecommendations', [...currentSelected, recommendationId]);
    } else {
      setValue(
        'selectedRecommendations',
        currentSelected.filter((id) => id !== recommendationId)
      );
    }
    trigger('selectedRecommendations');
  };

  const { mutate: shareToManager, isPending: isSharingToManager } = usePostManagerEmail({
    onSuccess: () => {
      logEvent(analytics, 'email_sent_to_manager', { custom_user_id: userId });
      // Close the original modal first, then show success modal
      onClose();
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 100); // Small delay to ensure smooth transition
    },
    onError: (error) => {
      console.error(error);
      onClose();
      setErrorMessage(error.message);
      setError({
        code: 500,
        message: 'Failed to share with manager',
      });
    },
  });

  const onSubmit = (data: ShareToManagerFormData) => {
    const ccEmails = data.cc
      ? data.cc
          .split(',')
          .map((email) => email.trim())
          .filter((email) => email !== '')
      : [];

    // Add current user email to CC if sendCopyToYourself is checked
    if (data.sendCopyToYourself && currentUserEmail !== 'unknown') {
      // Only add if not already in the CC list
      if (!ccEmails.includes(currentUserEmail)) {
        ccEmails.push(currentUserEmail);
      }
    }

    shareToManager({
      managerEmail: data.to,
      ccdEmail: ccEmails,
      resourceIds: data.selectedRecommendations,
      messageBody: emailContentStart,
    });
  };

  const isAnySelected = useMemo(
    () => selectedRecommendations.length > 0,
    [selectedRecommendations.length]
  );

  // Split recommendations into two columns
  const midPoint = Math.ceil(learningRecommendations.length / 2);
  const leftColumn = learningRecommendations.slice(0, midPoint);
  const rightColumn = learningRecommendations.slice(midPoint);

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      {/* Main Modal */}
      <Dialog
        open={isOpen}
        onClose={onClose}
        className="shareToManagerModal"
        maxWidth="lg"
        blockScroll={true}
        darkBackdrop={true}
        header={
          <Typography
            variant="h2"
            className="shareToManagerModalTitle"
          >
            {APP_TEXT.components.shareToManagerModal.title}
          </Typography>
        }
        footer={
          <div className="shareToManagerModalFooter">
            <PrimaryButton
              onClick={handleSubmit(onSubmit)}
              buttonSize={ButtonSize.Medium}
              disabled={!isFormValid || isValidating}
              loading={isSharingToManager}
              className="sendEmailButton"
            >
              {APP_TEXT.components.shareToManagerModal.buttons.sendEmail}
            </PrimaryButton>
            <SecondaryButton
              onClick={onClose}
              variant={ButtonVariant.Outline}
              buttonSize={ButtonSize.Medium}
            >
              {APP_TEXT.components.shareToManagerModal.buttons.close}
            </SecondaryButton>
          </div>
        }
      >
        <div className="shareToManagerModalContent">
          {/* Form Fields */}
          <div className="formFields">
            <div className="formSection">
              <div className="formRow">
                <FormField
                  label={APP_TEXT.components.shareToManagerModal.formFields.to}
                  helperText={APP_TEXT.components.shareToManagerModal.formFields.toHelperText}
                  error={errors.to?.message}
                >
                  <TextInput
                    className="inputField"
                    value={watch('to')}
                    onChange={(value: string) => {
                      setValue('to', value);
                      trigger('to');
                    }}
                    placeholder={APP_TEXT.components.shareToManagerModal.formFields.toPlaceholder}
                    ariaLabel={APP_TEXT.components.shareToManagerModal.accessibility.emailRecipientFieldLabel}
                    aria-invalid={errors.to ? 'true' : 'false'}
                  />
                </FormField>
                <FormField
                  label={APP_TEXT.components.shareToManagerModal.formFields.cc}
                  helperText={APP_TEXT.components.shareToManagerModal.formFields.ccHelperText}
                  error={errors.cc?.message}
                >
                  <TextInput
                    className="inputField ccInputField"
                    value={watch('cc') || ''}
                    onChange={(value: string) => {
                      setValue('cc', value);
                      trigger('cc');
                    }}
                    ariaLabel={APP_TEXT.components.shareToManagerModal.accessibility.emailCcFieldLabel}
                    aria-invalid={errors.cc ? 'true' : 'false'}
                  />
                </FormField>
              </div>
              {/* Send copy to yourself checkbox */}
              <div className="sendCopyToYourselfRow">
                <Checkbox
                  checked={watch('sendCopyToYourself') ?? false}
                  onChange={(value) => {
                    setValue('sendCopyToYourself', value);
                    trigger('sendCopyToYourself');
                  }}
                >
                  <Typography variant="body">
                    {APP_TEXT.components.shareToManagerModal.formFields.sendCopyToYourselfLabel.replace(
                      '[YOUR_EMAIL]',
                      currentUserEmail
                    )}
                  </Typography>
                </Checkbox>
              </div>
            </div>

            <ReadOnlyField
              label={APP_TEXT.components.shareToManagerModal.formFields.subjectLine}
              note={APP_TEXT.components.shareToManagerModal.emailContent.section1.readOnlyNote}
              labelId="emailSubjectLabel"
            >
              <InputText
                className="emailSubjectLine disabledField"
                name="Email subject text"
                aria-labelledby="emailSubjectLabel"
                aria-disabled="true"
                aria-readonly="true"
                value={APP_TEXT.components.shareToManagerModal.formFields.subjectLinePlaceholder.replace(
                  '[YOUR_NAME]',
                  currentUserFullName
                )}
                role="textbox"
                contentEditable="false"
              />
            </ReadOnlyField>
          </div>

          {/* Email Content Section 1 */}
          <ReadOnlyField
            label={APP_TEXT.components.shareToManagerModal.emailContent.section1.label}
            note={APP_TEXT.components.shareToManagerModal.emailContent.section1.readOnlyNote}
            labelId={"emailContentStartLabel"}
          >
            <InputTextarea
              className="emailContentStart disabledField"
              name="Opening email content"
              aria-labelledby="emailContentStartLabel"
              aria-disabled="true"
              aria-readonly="true"
              rows={9}
              value={emailContentStart}
              role="textbox"
              contentEditable="false"
            />
          </ReadOnlyField>

          {/* Learning Recommendations Section */}
          <div className="learningRecommendationsSection">
            <div className="recommendationsHeader">
              <div className="recommendationsHeaderContent">
                <label className="fieldLabel">
                  {APP_TEXT.components.shareToManagerModal.learningRecommendations.label}
                </label>
                <Typography
                  variant="body.xs"
                  className="selectNote"
                >
                  {APP_TEXT.components.shareToManagerModal.learningRecommendations.selectNote}
                </Typography>
                {errors.selectedRecommendations && (
                  <Typography
                    variant="body.xs"
                    className="errorText"
                  >
                    {errors.selectedRecommendations.message ||
                      'Please select at least one recommendation'}
                  </Typography>
                )}
              </div>
              <SecondaryButton
                variant={ButtonVariant.Outline}
                buttonSize={ButtonSize.Small}
                className="deselectAllButton"
                onClick={isAnySelected ? handleDeselectAll : handleSelectAll}
                icon={<i className={`fa-solid fa-${isAnySelected ? 'xmark' : 'check'}`}></i>}
              >
                {isAnySelected
                  ? APP_TEXT.components.shareToManagerModal.learningRecommendations.deselectAll
                  : APP_TEXT.components.shareToManagerModal.learningRecommendations.selectAll}
              </SecondaryButton>
            </div>

            <div className="recommendationsGrid">
              {[leftColumn, rightColumn].map((column, columnIndex) => (
                <div
                  key={columnIndex}
                  className="recommendationsColumn"
                >
                  {column.map((recommendation) => (
                    <RecommendationItem
                      key={recommendation.id}
                      recommendation={recommendation}
                      isSelected={selectedRecommendations.includes(recommendation.id)}
                      onToggle={handleRecommendationToggle}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Email Content Section 2 */}
          <ReadOnlyField
            label={APP_TEXT.components.shareToManagerModal.emailContent.section2.label}
            note={APP_TEXT.components.shareToManagerModal.emailContent.section2.readOnlyNote}
            labelId="emailContentContinued"
          >
            <InputTextarea
              className="emailContentEnd disabledField"
              name="Closing email content"
              aria-labelledby="emailContentContinued"
              aria-disabled="true"
              aria-readonly="true"
              rows={5}
              value={emailContentEnd}
              role="textbox"
              contentEditable="false"
            />
          </ReadOnlyField>

          {/* Error Message */}
          {errorMessage && (
            <div className="errorMessageContainer">
              <Typography
                variant="body.small"
                className="errorMessage"
              >
                {errorMessage}
              </Typography>
            </div>
          )}
        </div>
      </Dialog>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        className="shareToManagerSuccessModal"
        maxWidth="sm"
        blockScroll={true}
        darkBackdrop={true}
        header={
          <Typography
            variant="h2"
            className="successModalTitle"
          >
            {APP_TEXT.components.shareToManagerModal.successModal.title}
          </Typography>
        }
        footer={
          <div className="successModalFooter">
            <PrimaryButton
              onClick={handleSuccessModalClose}
              buttonSize={ButtonSize.Medium}
              className="successModalButton"
            >
              {APP_TEXT.components.shareToManagerModal.successModal.button}
            </PrimaryButton>
          </div>
        }
      >
        <div className="successModalContent">
          <Typography
            variant="body"
            className="successMessage"
          >
            {APP_TEXT.components.shareToManagerModal.successModal.message}
          </Typography>
        </div>
      </Dialog>
    </>
  );
}

export default ShareToManagerModal;
